import { StreamMap } from '@m43/core';
import { useApiClient } from '~/api-client';
import { MessagePayload } from '@m43/sockets-mark43';
import { EventPanelResource, PillUnit } from '@mark43/cad-api';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntegratedStreams } from '../../common/hooks/use-integrated-streams';

const streamsToSubscribeTo = ['nudge'] as const;

export function useIntegratedEventUnitsState(props: {
    eventId?: number;
    initialUnits?: PillUnit[];
}) {
    const apiClient = useApiClient();

    const hasBeenInitialized = useRef(false);
    const [units, setUnits] = useState<PillUnit[]>(props.initialUnits ?? []);
    const unitsById = useMemo(() => {
        return Object.fromEntries(units.map((unit) => [unit.id, unit]));
    }, [units]);
    useEffect(() => {
        if (props.initialUnits && !hasBeenInitialized.current) {
            hasBeenInitialized.current = true;
            setUnits(props.initialUnits);
        }
    }, [props.initialUnits]);

    const getCurrentUnits = useQuery(
        ['GetPillUnitsForEventPanel', props.eventId] as const,
        ({ queryKey }) => {
            const eventId = queryKey[1];
            if (eventId) {
                return apiClient.request<EventPanelResource.GetPillUnitsForEventPanel>({
                    method: 'GET',
                    url: `event_panel/${eventId}/units`,
                });
            } else {
                return Promise.resolve(null);
            }
        },
        {
            enabled: false,
            // If we do not set placeholder data, then when the event changes from
            // `new` to `persisted`, the query's loading state will change from `success`
            // to `loading`, and stay in a `loading` state indefinitely.

            // This occurs because when the query key changes, nothing inside of
            // `useNudge` tells this query to re-execute

            // And, if the query's loading state stays in `loading`, then the
            // `useNudge` hook will not call the `promiseBufferCallback` function

            // Setting placeholder data to `null` will cause the query to go into
            // a `success` state when the query key changes
            placeholderData: null,
        }
    );

    const eventId = props.eventId;
    const onMessage: (message: MessagePayload<StreamMap, 'nudge'>) => number[] = useCallback(
        (message) => {
            // we don't followup on unit nudges before the event exists, in particular to avoid
            // accidentally removing the unit pill for a self initiated unit
            if (!eventId) return [];
            const unitNudgeKeys = ['U'] as const; // read id as unit id
            const nonUnitNudgeKeys = ['UUL', 'ULI', 'UT', 'P', 'USHIFT'] as const; // read u as unit id
            const unitStatusNudgeKeys = ['USS', 'HU'] as const; // read e as event id
            const eventNudgeKeys = ['E', 'EC']; // read id as event id
            for (const key of unitNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (unitsById[entry.id]) {
                        return [eventId];
                    }
                }
            }
            for (const key of nonUnitNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (entry.u && unitsById[entry.u]) {
                        return [eventId];
                    }
                }
            }
            for (const key of unitStatusNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (eventId && entry.e === eventId) {
                        return [eventId];
                    }
                }
            }
            for (const key of eventNudgeKeys) {
                const entries = message.payload.map[key];
                if (!entries) continue;
                for (const entry of entries) {
                    if (eventId && entry.id === eventId) {
                        return [eventId];
                    }
                }
            }
            return [];
        },
        [eventId, unitsById]
    );

    useIntegratedStreams({
        streamNames: streamsToSubscribeTo,
        onMessage,
        query: getCurrentUnits,
        promiseBufferCallback: () => {
            return getCurrentUnits.refetch().then((response) => {
                if (response) {
                    const data = response?.data?.data?.data?.metadata.pillUnits;
                    if (data) {
                        setUnits(data);
                    }
                }
            });
        },
    });

    return {
        units,
    };
}
